import { Link, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBoxesStacked, faHeartPulse, faListCheck, faNetworkWired, faServer, faShieldHalved } from '@fortawesome/free-solid-svg-icons';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { useUser } from '../../context/UserContext';

interface HostingSection {
  id: 'hosting' | 'sites' | 'uptime' | 'network' | 'inventory' | 'audits';
  to: string;
  label: string;
  icon: IconDefinition;
  adminOnly?: boolean;
}

const SECTIONS: HostingSection[] = [
  { id: 'hosting', to: '/hosting', label: 'Hosting', icon: faServer },
  { id: 'sites', to: '/hosting/inventory', label: 'Site inventory', icon: faListCheck },
  { id: 'uptime', to: '/uptime', label: 'Uptime', icon: faHeartPulse },
  { id: 'network', to: '/private-network', label: 'Private Network', icon: faNetworkWired },
  { id: 'inventory', to: '/security', label: 'Security inventory', icon: faBoxesStacked, adminOnly: true },
  { id: 'audits', to: '/security?view=audits', label: 'Security audits', icon: faShieldHalved, adminOnly: true },
];

function activeSection(pathname: string, search: string): HostingSection['id'] | null {
  if (pathname.startsWith('/hosting/inventory')) return 'sites';
  if (pathname.startsWith('/hosting')) return 'hosting';
  if (pathname.startsWith('/uptime')) return 'uptime';
  if (pathname.startsWith('/private-network')) return 'network';
  if (pathname.startsWith('/security')) return new URLSearchParams(search).get('view') === 'audits' ? 'audits' : 'inventory';
  return null;
}

/**
 * The submenu under Sidebar → Hosting: Hosting, Site inventory, Uptime, Private Network, Security inventory and Security audits are one area with one
 * nav item, switched here. Same chip row as Agents. Security views are admin-only, like their routes.
 */
export default function HostingNav() {
  const { pathname, search } = useLocation();
  const { user } = useUser();
  const isAdmin = user?.role?.trim?.().toLowerCase?.() === 'admin';
  const active = activeSection(pathname, search);
  const sections = SECTIONS.filter((s) => !s.adminOnly || isAdmin);

  return (
    <nav
      aria-label="Hosting"
      style={{
        padding: '0 0 10px',
        borderBottom: '1px solid var(--border-hairline)',
        marginTop: 'calc(var(--space-4) * -1)',
        marginBottom: 'var(--space-5)',
      }}
    >
      {/* Phones: a 2×2 grid of finger-sized chips instead of a ragged wrap. */}
      <div className="grid w-full grid-cols-2 md:flex md:w-auto md:flex-wrap md:items-center" style={{ gap: '6px' }}>
        {sections.map((s) => {
          const on = s.id === active;
          return (
            <Link
              key={s.id}
              to={s.to}
              aria-current={on ? 'page' : undefined}
              className="pressable min-h-[40px] md:min-h-0"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                padding: '6px 12px',
                borderRadius: 'var(--radius-sm)',
                border: '1px solid ' + (on ? 'var(--border-strong)' : 'transparent'),
                background: on ? 'var(--surface-active)' : 'transparent',
                color: on ? 'var(--text-body)' : 'var(--text-muted)',
                fontFamily: 'var(--font-ui)',
                fontSize: 'var(--text-sm)',
                fontWeight: (on ? 'var(--weight-medium)' : 'var(--weight-regular)') as React.CSSProperties['fontWeight'],
                whiteSpace: 'nowrap',
                textDecoration: 'none',
                transition: 'background var(--dur-base) var(--ease-out), border-color var(--dur-base) var(--ease-out), color var(--dur-base) var(--ease-out)',
              }}
            >
              <FontAwesomeIcon icon={s.icon} style={{ fontSize: '12px', width: '14px', color: on ? 'var(--accent-text)' : undefined }} />
              {s.label}
            </Link>
          );
        })}
      </div>
    </nav>
  );
}
